import { useEffect, useMemo, useRef, useState } from 'react';
import { useV2 } from './ctx';
import { pathWith, rp, rpS, jt1, shortDate, digits, amtOf, sgnOf, dayOf } from './model';
import { Sheet, Field, niceStep } from './ui';

const KIND = { reopen: 'Koreksi jumlah dibayar', reverse: 'Batalkan pembayaran', amount_change: 'Ubah jumlah', date_change: 'Pindah tanggal', cancel_line: 'Batal', add_disbursement: 'Tambah keluar', add_inflow: 'Tambah masuk', add_line: 'Tambah', description_change: 'Ubah keterangan', catat_bayar: 'Bayar beda dari plan' };
const when = t => `${new Date(t).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })} ${new Date(t).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}`;

// Perubahan: the Director's approval inbox (with the exact effect of the
// ticked items on month-end and the lowest day), staff's own requests, and
// the history of every decided change, biggest effect first.
export default function Perubahan() {
  const { model, role, data } = useV2();
  const [seg, setSeg] = useState('menunggu');
  if (!model) return <div className="v2-page"><div className="card">Belum ada plan untuk bulan ini.</div></div>;
  // same count as the nav badge: corrections to paid transactions are the Director's too
  const n = model.items.length + (role === 'director' ? (data?.reopen?.pending || 0) : 0);
  return (
    <div className="v2-page">
      <div className="v2-head"><div><h2>Perubahan</h2><p>{role === 'director' ? 'Semua yang menggeser plan, dan dampaknya ke saldo akhir.' : 'Pengajuan Anda dan riwayat keputusan Direktur.'}</p></div>
        <div className="seg">{[['menunggu', `Menunggu${n ? ` · ${n}` : ''}`], ['riwayat', 'Riwayat']].map(([k, l]) => <button key={k} type="button" className={seg === k ? 'on' : ''} onClick={() => setSeg(k)}>{l}</button>)}</div>
      </div>
      {seg === 'menunggu' ? (role === 'director' ? <Inbox /> : <Mine />) : <Riwayat />}
    </div>
  );
}

// Group pending items into bursts: same requester, < 20 min apart.
function bursts(items) {
  const sorted = [...items].sort((a, b) => new Date(a.at) - new Date(b.at));
  const out = [];
  sorted.forEach(it => {
    const g = out[out.length - 1];
    if (g && g.by === it.by && new Date(it.at) - new Date(g.last) < 20 * 60 * 1000) { g.items.push(it); g.last = it.at; }
    else out.push({ by: it.by, first: it.at, last: it.at, items: [it] });
  });
  return out.reverse();
}

// Within a burst, changes to the same line are one packet (Director, 30 Oct:
// "isnt it more coherent if i see the changes as packeted together...so like
// she changed the value AND the date AND the truck for this line. and then we
// can drill into why"). Order kept: a packet sits where its first change was.
const PACKABLE = ['amount_change', 'date_change', 'description_change', 'cancel_line'];
function packets(items) {
  const out = [], byLine = {};
  items.forEach(it => {
    const k = it.src === 'amendment' && it.lineId && PACKABLE.includes(it.kind) ? it.lineId : null;
    if (k && byLine[k]) { byLine[k].items.push(it); return; }
    const p = { key: k ? 'p:' + k : it.key, items: [it] };
    if (k) byLine[k] = p;
    out.push(p);
  });
  return out;
}

// Corrections to transactions already paid (reopen = different amount, reverse
// = cancel the payment). They used to sit on top as the old v1 cards with their
// own buttons (Director, 27 Oct: "what is this nonsense UI?"); now they're rows
// in the same list, ticked and decided with everything else.
function useFixes(model, data) {
  const { api } = useV2();
  const [raw, setRaw] = useState({ reopens: [], reverses: [] });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    Promise.all([api.listPendingReopens().catch(() => ({})), api.listPendingReverseRequests().catch(() => ({}))])
      .then(([o, v]) => live && setRaw({ reopens: o.reopenRequests || [], reverses: v.reverseRequests || [] }));
    return () => { live = false; };
  }, [api, tick, data]);
  const items = useMemo(() => {
    const reqById = Object.fromEntries((data.requests?.items || []).map(r => [r.id, r]));
    const zero = () => Array(model.nDays).fill(0);
    const reopen = raw.reopens.map(r => {
      const q = r.requests || {}, l = model.lineById[reqById[r.request_id]?.plan_line_id] || q.plan_lines;
      const sign = l ? sgnOf(l) : -1, day = (l && l.tanggal) || q.request_date;
      const diff = sign * (Number(r.new_amount || 0) - Number(r.old_amount || 0));
      const eff = zero(), i = model.dates.indexOf(day); if (i >= 0) eff[i] += diff;
      // a payment from an earlier month moves that month's closing, not this month's end
      const other = i < 0 && day ? new Date(day + 'T00:00:00').toLocaleDateString('id-ID', { month: 'short' }) : null;
      return { other, otherD: diff, key: 'o:' + r.id, src: 'reopen', id: r.id, kind: 'reopen', title: q.keterangan || '—', date: day, reason: r.reason, by: r.requested_by, at: r.created_at, v1Val: '—', before: rp(r.old_amount), after: rp(r.new_amount), eff, delta: eff.reduce((a, x) => a + x, 0) };
    });
    // a cancelled payment goes back to "belum dibayar": its effect depends on the plan line, so it isn't counted
    const reverse = raw.reverses.map(r => { const q = r.requests || {};
      return { key: 'v:' + r.id, src: 'reverse', id: r.id, kind: 'reverse', title: q.keterangan || '—', date: q.request_date, reason: r.reason, by: r.requested_by, at: r.created_at, v1Val: '—', before: rp(q.amount), after: 'batal', eff: zero(), delta: null };
    });
    return [...reopen, ...reverse].sort((a, b) => new Date(a.at) - new Date(b.at));
  }, [raw, model, data]);
  return { fixes: items, reloadFixes: () => setTick(t => t + 1) };
}

function Inbox() {
  const { model, api, done, isPhone, data } = useV2();
  const { fixes, reloadFixes } = useFixes(model, data);
  const groups = useMemo(() => bursts(model.items), [model.items]);
  const all = useMemo(() => [...model.items, ...fixes], [model.items, fixes]);
  // Everything starts ticked, including requests that arrive while the page is open.
  const [sel, setSel] = useState(() => new Set(model.items.map(i => i.key)));
  const seen = useRef(new Set(model.items.map(i => i.key)));
  useEffect(() => {
    const fresh = all.map(i => i.key).filter(k => !seen.current.has(k));
    fresh.forEach(k => seen.current.add(k));
    if (fresh.length) setSel(s => new Set([...s, ...fresh]));
  }, [all]);
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const keys = [...sel].filter(k => all.some(i => i.key === k));
  const toggle = k => setSel(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const setMany = (its, on) => setSel(s => { const n = new Set(s); its.forEach(i => (on ? n.add(i.key) : n.delete(i.key))); return n; });
  const toggleGroup = g => setSel(s => { const n = new Set(s); const all = g.items.every(i => n.has(i.key)); g.items.forEach(i => (all ? n.delete(i.key) : n.add(i.key))); return n; });

  // Decisions go out in chunks with a progress count (Director, 14 Oct: 81
  // approvals in one request took ~3 min, the browser gave up with "failed
  // to fetch" while the server kept going, and the list looked half-done).
  // A chunk that loses its connection is retried; anything the server
  // already decided comes back "not pending" and counts as done, so a retry
  // can never approve twice. One batchId for all chunks = one ledger row.
  const [progress, setProgress] = useState(null);
  const run = async (approve, note) => {
    setBusy(true);
    const chosen = all.filter(i => sel.has(i.key));
    const am = chosen.filter(i => i.src === 'amendment').map(i => i.id), rq = chosen.filter(i => i.src === 'request').map(i => i.id);
    const fx = chosen.filter(i => i.src === 'reopen' || i.src === 'reverse');
    const total = am.length + rq.length + fx.length, CHUNK = 20;
    const batchId = window.crypto?.randomUUID ? window.crypto.randomUUID() : undefined;
    const errs = [];
    let n = 0, lost = 0;
    setProgress({ n, total });
    const alreadyDone = m => /is not pending/i.test(m || '');
    const send = async (fn, part) => {
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          const r = await fn(part);
          (r?.failed || []).forEach(f => { if (!alreadyDone(f.error)) errs.push(f.error); });
          return;
        } catch (e) {
          if (e.body) { errs.push(e.message); return; } // the server answered with an error: don't retry
          await new Promise(res => setTimeout(res, 2000 * (attempt + 1))); // connection lost: wait, then ask again
        }
      }
      lost += part.length;
    };
    const chunks = (ids, fn) => ids.reduce((p, _, i) => (i % CHUNK ? p : p.then(async () => { const part = ids.slice(i, i + CHUNK); await send(fn, part); n += part.length; setProgress({ n, total }); })), Promise.resolve());
    await chunks(am, part => (approve ? api.bulkApproveAmendments(part, batchId) : api.bulkRejectAmendments(part, note)));
    await chunks(rq, async part => {
      if (approve) return api.bulkApproveRequests(part);
      const failed = [];
      for (const id of part) { try { await api.rejectRequest(id, note); } catch (e) { if (e.body) failed.push({ id, error: e.message }); else throw e; } }
      return { failed };
    });
    for (const it of fx) {
      try {
        if (it.src === 'reopen') await (approve ? api.approveReopen(it.id) : api.rejectReopen(it.id, note));
        else await (approve ? api.approveReverseRequest(it.id) : api.rejectReverseRequest(it.id, note));
      } catch (e) { if (!alreadyDone(e.message)) errs.push(`${it.title}: ${e.message}`); }
      n += 1; setProgress({ n, total });
    }
    reloadFixes();
    if (errs.length) alert([...new Set(errs)].join('\n'));
    if (lost) alert(`Koneksi terputus untuk ${lost} pengajuan. Daftar dimuat ulang: yang sudah diproses tidak muncul lagi, sisanya bisa diulang.`);
    // stay busy until the refreshed list is on screen, so decided items can't be clicked twice
    await done(`${total - errs.length - lost} pengajuan ${approve ? 'disetujui' : 'ditolak'}`);
    setBusy(false); setRejecting(false); setProgress(null);
  };

  if (!all.length) return (
    <div className="card" style={{ textAlign: 'center', padding: 28 }}><b style={{ font: '600 18px var(--display)' }}>Tidak ada yang menunggu</b><p className="hint">Saldo akhir yang disetujui: {rp(model.settledEnd)}</p></div>
  );
  const fixAll = fixes.length > 0 && fixes.every(i => sel.has(i.key));
  const toggleFixes = () => setSel(s => { const n = new Set(s); fixes.forEach(i => (fixAll ? n.delete(i.key) : n.add(i.key))); return n; });
  const fixD = fixes.reduce((t, i) => t + (i.delta || 0), 0);
  const head = (checked, onChange, text, d) => (
    <div className="lbl"><label className="pr-head"><input type="checkbox" checked={checked} onChange={onChange} />{text}</label>{d ? <span className={`amt ${d < 0 ? 'bad' : 'pos'}`}>{rpS(d)}</span> : null}</div>
  );
  return (
    <>
      <div className="cols" style={{ gridTemplateColumns: isPhone ? '1fr' : 'minmax(0,1fr) 380px', alignItems: 'start' }}>
        {/* minmax(0,1fr): a long title must wrap, not widen the list under the sticky panel (27 Oct) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: 14 }}>
          {fixes.length > 0 && <div>
            {head(fixAll, toggleFixes, `Koreksi transaksi yang sudah dibayar · ${fixes.length}`, fixD)}
            <div className="group">{fixes.map(it => <Item key={it.key} it={it} on={sel.has(it.key)} toggle={() => toggle(it.key)} />)}</div>
          </div>}
          {groups.map(g => { const allOn = g.items.every(i => sel.has(i.key)); const d = g.items.reduce((s, i) => s + i.delta, 0); return (
            <div key={g.first}>
              {head(allOn, () => toggleGroup(g), `${g.by || '—'} · ${when(g.first)} · ${g.items.length} perubahan`, d)}
              <div className="group">{packets(g.items).map(p => (p.items.length > 1
                ? <Packet key={p.key} p={p} sel={sel} toggle={toggle} setMany={setMany} />
                : <Item key={p.key} it={p.items[0]} on={sel.has(p.items[0].key)} toggle={() => toggle(p.items[0].key)} />))}</div>
            </div>
          ); })}
        </div>
        <Impact keys={keys} extra={fixes} busy={busy} progress={progress} onApprove={() => run(true)} onReject={() => setRejecting(true)} />
      </div>
      {rejecting && <RejectSheet n={keys.length} busy={busy} progress={progress} onClose={() => setRejecting(false)} onConfirm={note => run(false, note)} />}
    </>
  );
}

// One pending change as a ledger row (27 Oct): what it is on the left, what
// changes on the right (before → after), and its effect on month-end under it.
// The old Plan awal / Disetujui / Diajukan grid was mostly dashes.
const isAdd = it => ['add_disbursement', 'add_inflow', 'add_line'].includes(it.kind);
function changeOf(it) {
  if (it.kind === 'cancel_line') return `batal ${it.before}`;
  if (it.kind === 'reverse') return `batal ${it.before}`;
  if (['add_disbursement', 'add_inflow', 'add_line'].includes(it.kind)) return it.after;
  if (it.kind === 'description_change') return 'keterangan baru';
  return it.before && it.before !== '—' ? `${it.before} → ${it.after}` : it.after;
}
// sub: a row inside a packet's rincian; the packet already shows the line's
// name and reason, so the row says only which change it is
function Item({ it, on, toggle, right, sub }) {
  const d = it.delta;
  const v1 = it.v1Val && it.v1Val !== '—' && it.v1Val !== it.before ? `plan awal ${it.v1Val}` : '';
  const meta = sub ? v1 : [KIND[it.kind] || it.kind, it.date ? shortDate(it.date) : '', toggle ? '' : when(it.at), v1, it.reason ? `"${it.reason}"` : ''].filter(Boolean).join(' · ');
  const Tag = toggle ? 'label' : 'div';
  return (
    <Tag className={`row pr-row${toggle ? ' tap' : ''}${toggle && !on ? ' off' : ''}`}>
      {toggle && <input type="checkbox" checked={on} onChange={toggle} aria-label={`Pilih ${it.title}`} />}
      <span className="pr-main"><span className="pr-t">{sub ? KIND[it.kind] || it.kind : it.title}</span>{meta && <span className="pr-m" title={meta}>{meta}</span>}</span>
      {isAdd(it)
        // an added line's amount is its effect: one coloured figure, not the same number twice
        ? <span className="pr-eff"><span className={`pr-chg ${d < 0 ? 'bad' : d > 0 ? 'pos' : ''}`}>{changeOf(it)}</span>{right && <span className="pr-act">{right}</span>}</span>
        : <span className="pr-eff"><span className="pr-chg">{changeOf(it)}</span>
          <span className={`pr-d ${it.other || d == null || !d ? 'muted' : d < 0 ? 'bad' : 'pos'}`}>{it.other ? `saldo ${it.other} ${it.otherD ? rpS(it.otherD) : '±0'}` : d == null ? 'kembali belum dibayar' : d ? `saldo akhir ${rpS(d)}` : 'saldo akhir ±0'}</span>
          {right && <span className="pr-act">{right}</span>}</span>}
    </Tag>
  );
}

// One line's changes as one row (Director, 30 Oct: "she changed the value AND
// the date AND the truck for this line. and then we can drill into why"):
// each change on its own line, the reason in full under the name (never cut
// off: it is the why), the summed effect; "rincian" opens each change with its
// own tick. A truck swap applies at once (UbahBaris.js) and rides in the
// reason as " · truk A → B": shown here as a change, marked already done.
const SHORT = { amount_change: 'jumlah', date_change: 'tanggal', description_change: 'keterangan', cancel_line: 'batal' };
const TRUCK = /\s·\struk (\S+) → (\S+)$/;
function Packet({ p, sel, toggle, setMany }) {
  const [open, setOpen] = useState(false);
  const on = p.items.every(i => sel.has(i.key)), some = p.items.some(i => sel.has(i.key));
  const first = p.items[0], d = p.items.reduce((t, i) => t + (i.delta || 0), 0);
  const raw = [...new Set(p.items.map(i => i.reason).filter(Boolean))];
  const truck = raw.map(r => r.match(TRUCK)).find(Boolean);
  const reasons = [...new Set(raw.map(r => r.replace(TRUCK, '')))];
  return (
    <div className="pr-packet">
      <label className={`row pr-row tap${!on ? ' off' : ''}`}>
        <input type="checkbox" checked={on} ref={el => { if (el) el.indeterminate = some && !on; }} onChange={() => setMany(p.items, !on)} aria-label={`Pilih semua perubahan ${first.title}`} />
        <span className="pr-main">
          <span className="pr-t">{first.title}</span>
          {reasons.map(r => <span key={r} className="pr-why">"{r}"</span>)}
          <span className="pr-m">{p.items.length} perubahan{truck ? ' + ganti truk' : ''} · {first.date ? shortDate(first.date) : ''}</span>
        </span>
        <span className="pr-eff">
          {p.items.map(i => <span key={i.key} className="pr-chg"><span className="pr-k">{SHORT[i.kind]}</span> {changeOf(i)}</span>)}
          {truck && <span className="pr-chg muted" title="Ganti truk langsung tersimpan, tidak perlu disetujui"><span className="pr-k">truk</span> {truck[1]} → {truck[2]} ✓</span>}
          <span className={`pr-d ${!d ? 'muted' : d < 0 ? 'bad' : 'pos'}`}>{d ? `saldo akhir ${rpS(d)}` : 'saldo akhir ±0'}</span>
          <button type="button" className="linkbtn pr-more" onClick={e => { e.preventDefault(); setOpen(o => !o); }}>{open ? 'tutup ▴' : 'rincian ▾'}</button>
        </span>
      </label>
      {open && <div className="pr-sub">{p.items.map(it => <Item key={it.key} it={it} sub on={sel.has(it.key)} toggle={() => toggle(it.key)} />)}</div>}
    </div>
  );
}

// What the ticked items do to the month: exact rupiah, before → after, and
// a chart of the days they touch (concise even when 28 days are left).
function Impact({ keys, extra = [], busy, progress, onApprove, onReject }) {
  const { model } = useV2();
  const ks = new Set(keys);
  const after = pathWith(model, ks, extra);
  const n = model.nDays, endB = model.settled[n - 1], endA = after[n - 1];
  const lowB = Math.min(...model.settled), lowA = Math.min(...after);
  const lowBi = model.settled.indexOf(lowB), lowAi = after.indexOf(lowA);
  const first = after.findIndex((v, i) => Math.round(v) !== Math.round(model.settled[i]));
  // Days that only go below zero because of the ticked items; days already
  // negative in the approved plan aren't this decision's doing.
  const newNeg = after.map((v, i) => (v < 0 && model.settled[i] >= 0 ? i + 1 : null)).filter(Boolean);
  const line = (k, b, a, extra) => (
    <div style={{ display: 'grid', gap: 2 }}>
      <span style={{ fontSize: 11, opacity: .7 }}>{k}</span>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span className="num" style={{ fontSize: 13.5 }}>{rp(b)} → <b>{rp(a)}</b>{extra}</span>
        <span className="num" style={{ fontWeight: 600, color: a - b < 0 ? '#F4A3A3' : a - b > 0 ? '#9CD2AE' : 'inherit' }}>{a === b ? '±0' : rpS(a - b)}</span>
      </div>
    </div>
  );
  return (
    <div className="card" style={{ position: 'sticky', top: 12 }}>
      <div className="lbl" style={{ padding: 0 }}><span>{keys.length} dipilih</span><span>kalau disetujui</span></div>
      <div className="impact">
        {line(`Saldo akhir ${shortDate(model.dates[n - 1])}`, endB, endA)}
        {line('Saldo terendah', lowB, lowA, <span style={{ opacity: .7 }}> · tgl {lowBi === lowAi ? lowAi + 1 : `${lowBi + 1} → ${lowAi + 1}`}</span>)}
      </div>
      {newNeg.length > 0 && <div className="warnbar">Jadi minus di tanggal {newNeg.slice(0, 6).join(', ')}{newNeg.length > 6 ? ', …' : ''}</div>}
      {first >= 0 ? <ImpactChart from={Math.max(0, first - 1)} before={model.settled} after={after} dates={model.dates} /> : <p className="hint">Tidak menggeser saldo harian.</p>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <button type="button" className="btn ghost" disabled={busy || !keys.length} onClick={onReject}>Tolak {keys.length || ''}</button>
        <button type="button" className="btn" disabled={busy || !keys.length} onClick={onApprove}>{busy ? (progress ? `${progress.n}/${progress.total}…` : 'Memproses…') : `Setujui ${keys.length || ''}`}</button>
      </div>
    </div>
  );
}

function ImpactChart({ from, before, after, dates }) {
  const b = before.slice(from), a = after.slice(from), N = b.length;
  const W = 360, H = 150, l = 34, r = 10, t = 10, bt = 20;
  const all = b.concat(a, [0]);
  const step = niceStep((Math.max(...all) - Math.min(...all)) / 3 || 1e6);
  const top = Math.ceil(Math.max(...all) / step) * step, bot = Math.floor(Math.min(...all) / step) * step;
  const X = i => l + (N === 1 ? 0 : i / (N - 1)) * (W - l - r), Y = v => t + (top - v) / (top - bot || 1) * (H - t - bt);
  const pts = arr => arr.map((v, i) => `${X(i).toFixed(1)},${Y(v).toFixed(1)}`).join(' ');
  const ticks = []; for (let v = bot; v <= top + 1; v += step) ticks.push(v);
  const lab = [0, Math.floor((N - 1) / 2), N - 1].filter((v, i, s) => s.indexOf(v) === i);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Saldo harian sebelum dan sesudah">
      {ticks.map(v => <g key={v}><line x1={l} x2={W - r} y1={Y(v)} y2={Y(v)} stroke="#E2D9CC" strokeWidth={v === 0 ? 1.4 : 1} /><text x={l - 4} y={Y(v) + 3} textAnchor="end" fontSize="9" fill="#7A6F63" fontFamily="JetBrains Mono,monospace">{Math.round(v / 1e6)}</text></g>)}
      <polyline fill="none" points={pts(b)} stroke="#2B2420" strokeWidth="1.8" />
      <polyline fill="none" points={pts(a)} stroke="#C98A2E" strokeWidth="2.4" strokeDasharray="1.5 3" strokeLinecap="round" />
      <text x={W - r} y={Y(a[N - 1]) + (a[N - 1] < b[N - 1] ? 12 : -5)} textAnchor="end" fontSize="9.5" fontWeight="600" fill="#9A5B0A" fontFamily="JetBrains Mono,monospace">{jt1(a[N - 1])}</text>
      {lab.map(i => <text key={i} x={X(i)} y={H - 5} textAnchor="middle" fontSize="9" fill="#7A6F63" fontFamily="Inter,sans-serif">{shortDate(dates[from + i])}</text>)}
    </svg>
  );
}

function RejectSheet({ n, busy, progress, onClose, onConfirm }) {
  const [note, setNote] = useState('');
  return (
    <Sheet title={`Tolak ${n} pengajuan`} onClose={onClose}>
      <div className="fld"><Field label="Catatan untuk pengaju"><input value={note} onChange={e => setNote(e.target.value)} placeholder="mis. tunggu konfirmasi pabrik" autoFocus /></Field></div>
      <p className="hint">Baris kembali ke nilai yang disetujui.</p>
      <div className="act"><button type="button" className="btn" style={{ background: "var(--red)" }} disabled={busy} onClick={() => onConfirm(note || 'Ditolak')}>{busy ? (progress ? `${progress.n}/${progress.total}…` : 'Memproses…') : 'Tolak'}</button></div>
    </Sheet>
  );
}

// Staff: what I sent that is still waiting, with Tarik; recent rejections.
function Mine() {
  const { model, api, user, done, data, open } = useV2();
  const mine = model.items.filter(i => i.by === user.name);
  const others = model.items.filter(i => i.by !== user.name);
  const [busyId, setBusyId] = useState(null);
  const tarik = async it => { if (busyId) return; setBusyId(it.id); try { await api.withdrawAmendment(it.id); await done('Pengajuan ditarik'); } catch (e) { alert(e.message); } setBusyId(null); };
  const rejected = (data.amendments?.items || []).filter(a => a.status === 'rejected' && a.requested_by === user.name && a.rejection_note !== 'Ditarik oleh pengaju')
    .sort((a, b) => new Date(b.approved_at || b.created_at) - new Date(a.approved_at || a.created_at)).slice(0, 10);
  const again = a => { const l = model.lineById[a.plan_line_id]; if (!l) return; open('ubah', { line: l, mode: a.amendment_type === 'date_change' ? 'tanggal' : a.amendment_type === 'cancel_line' ? 'batal' : a.amendment_type === 'description_change' ? 'keterangan' : 'jumlah' }); };
  return (
    // minmax(0,1fr): a long one-line description must not widen the page (27 Oct, staff phone was 526px wide)
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: 14 }}>
      <div>
        <div className="lbl"><span>Pengajuan saya · menunggu Direktur</span>{mine.length > 0 && <span>saldo akhir {rpS(mine.reduce((s, i) => s + i.delta, 0))}</span>}</div>
        {mine.length ? <div className="group">{mine.map(it => <Item key={it.key} it={it} right={it.src === 'amendment' ? <button type="button" className="btn ghost small" disabled={!!busyId} onClick={() => tarik(it)}>{busyId === it.id ? 'Menarik…' : 'Tarik'}</button> : null} />)}</div>
          : <div className="card hint">Tidak ada pengajuan yang menunggu.</div>}
      </div>
      {others.length > 0 && <div><div className="lbl"><span>Dari pengguna lain</span></div><div className="group">{others.map(it => <Item key={it.key} it={it} />)}</div></div>}
      {rejected.length > 0 && <div>
        <div className="lbl"><span>Ditolak</span></div>
        <div className="group">{rejected.map(a => <div key={a.id} className="row"><div className="grow"><div className="t">{a.plan_lines?.keterangan || (() => { try { return JSON.parse(a.requested_value).keterangan; } catch { return 'Baris baru'; } })()}</div><div className="m">{KIND[a.amendment_type] || a.amendment_type} · {a.rejection_note ? `"${a.rejection_note}"` : 'tanpa catatan'}</div></div>{model.lineById[a.plan_line_id] && <button type="button" className="btn ghost small" onClick={() => again(a)}>Ajukan lagi</button>}</div>)}</div>
      </div>}
    </div>
  );
}

// Effect of a decided amendment on month-end (approved ones moved the
// needle; rejected/withdrawn ones are shown for the record).
// cancel_line stores the line as text: "UJ Lampung 9511 · Pengeluaran Rp 5.600.000".
const cancelled = a => { const m = /(Pengeluaran|Penerimaan) Rp ([\d.]+)/.exec(a.original_value || ''); return m ? (m[1] === 'Penerimaan' ? 1 : -1) * digits(m[2]) : null; };
function effectOf(a) {
  const l = a.plan_lines; const s = l ? sgnOf(l) : -1;
  if (a.amendment_type === 'cancel_line') { const c = cancelled(a); return -(c ?? (l ? s * amtOf(l) : 0)); }
  if (a.amendment_type === 'amount_change') return s * (digits(a.requested_value) - digits(a.original_value));
  if (['add_disbursement', 'add_inflow', 'add_line'].includes(a.amendment_type)) { try { const v = JSON.parse(a.requested_value); return (v.penerimaan || 0) - (v.pengeluaran || 0); } catch { return 0; } }
  if (a.amendment_type === 'date_change' && l) { const mk = String(a.original_value || '').slice(0, 7); return String(a.requested_value || '').slice(0, 7) !== mk ? -s * amtOf(l) : 0; }
  return 0;
}

function Riwayat() {
  const { data, model } = useV2();
  const [f, setF] = useState('approved');
  const [sort, setSort] = useState('dampak');
  const [q, setQ] = useState('');
  // A line approved twice by duplicate add_* submissions exists once; show it once (same rule as /api/v2/history).
  const seenAdd = new Set();
  const am = (data.amendments?.items || []).filter(a => a.status !== 'pending')
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    .filter(a => { if (a.status !== 'approved' || !a.amendment_type.startsWith('add') || !a.plan_line_id) return true; if (seenAdd.has(a.plan_line_id)) return false; seenAdd.add(a.plan_line_id); return true; })
    .map(a => ({
    key: a.id, at: a.approved_at || a.created_at, status: a.rejection_note === 'Ditarik oleh pengaju' ? 'withdrawn' : a.status, kind: a.amendment_type, by: a.requested_by, reason: a.reason, note: a.rejection_note,
    title: a.plan_lines?.keterangan || (() => { try { return JSON.parse(a.requested_value).keterangan; } catch { return 'Baris baru'; } })(),
    date: a.plan_lines?.tanggal, before: a.amendment_type === 'add_disbursement' || a.amendment_type === 'add_inflow' ? '—' : a.amendment_type === 'date_change' ? shortDate(a.original_value) : a.amendment_type === 'cancel_line' ? (cancelled(a) != null ? rpS(cancelled(a)) : '—') : a.original_value, after: a.amendment_type === 'cancel_line' ? 'batal' : a.amendment_type === 'date_change' ? shortDate(a.requested_value) : (a.amendment_type.startsWith('add') ? '' : a.requested_value),
    d: effectOf(a),
  }));
  // Payments recorded at a different amount than the line's plan.
  const rq = (data.requests?.items || []).filter(r => r.status === 'approved' && r.plan_line_id && model.lineById[r.plan_line_id]).map(r => {
    const l = model.lineById[r.plan_line_id]; const planned = l.v1_amount ?? amtOf(l);
    return { key: 'r' + r.id, at: r.approved_at || r.created_at, status: 'approved', kind: 'catat_bayar', by: r.requested_by, reason: r.ops_note, title: r.keterangan, date: l.tanggal, before: rp(planned), after: rp(r.amount), d: sgnOf(l) * (r.amount - planned) };
  }).filter(r => Math.abs(r.d) >= 1000);
  const statusOk = s => (f === 'all' ? true : f === 'approved' ? s === 'approved' : s !== 'approved');
  const qq = q.trim().toLowerCase();
  const rows = am.concat(rq).filter(r => statusOk(r.status) && (!qq || `${r.title} ${r.reason || ''}`.toLowerCase().includes(qq)))
    .sort(sort === 'dampak' ? (a, b) => Math.abs(b.d) - Math.abs(a.d) : (a, b) => new Date(b.at) - new Date(a.at));
  const shown = rows.slice(0, 150);
  const pill = s => (s === 'approved' ? <span className="pill p-done">disetujui</span> : s === 'withdrawn' ? <span className="pill p-neutral">ditarik</span> : <span className="pill p-neutral">ditolak</span>);
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="seg">{[['approved', 'Disetujui'], ['no', 'Ditolak / ditarik'], ['all', 'Semua']].map(([k, l]) => <button key={k} type="button" className={f === k ? 'on' : ''} onClick={() => setF(k)}>{l}</button>)}</div>
        <div className="seg">{[['dampak', 'Dampak terbesar'], ['baru', 'Terbaru']].map(([k, l]) => <button key={k} type="button" className={sort === k ? 'on' : ''} onClick={() => setSort(k)}>{l}</button>)}</div>
        <input className="search" placeholder="Cari keterangan…" value={q} onChange={e => setQ(e.target.value)} style={{ flex: '1 1 160px' }} />
      </div>
      {/* No grand total here: the raw log can't reconcile to month-end on its own
          (corrections are logged elsewhere, a line can be cancelled then re-added).
          Ringkasan › Penyebab perubahan saldo akhir is the reconciled answer. */}
      <div className="lbl"><span>{rows.length} perubahan · bayar beda plan dihitung dari plan awal</span><span>dampak ke saldo akhir</span></div>
      <div className="group">
        {shown.map(r => (
          <div key={r.key} className="row" style={{ alignItems: 'flex-start' }}>
            <div className="grow" style={{ minWidth: 0 }}>
              <div className="t">{r.title}</div>
              <div className="m">{KIND[r.kind] || r.kind}{r.date ? ` · ${shortDate(dayOf(r.date))}` : ''} · {r.before || '—'}{r.after ? ` → ${r.after}` : ''} · {r.by || '—'}, {when(r.at)}{r.reason ? ` · "${r.reason}"` : ''}{r.note && r.status !== 'approved' ? ` · catatan: "${r.note}"` : ''}</div>
            </div>
            <div style={{ display: 'grid', gap: 4, justifyItems: 'end' }}>{pill(r.status)}<span className={`amt ${r.status !== 'approved' ? 'muted' : r.d < 0 ? 'bad' : r.d > 0 ? 'pos' : 'muted'}`}>{r.d ? rpS(r.d) : '±0'}</span></div>
          </div>
        ))}
        {!rows.length && <div className="row hint">Belum ada.</div>}
      </div>
      {rows.length > shown.length && <p className="hint">Menampilkan 150 dari {rows.length}. Persempit dengan pencarian.</p>}
    </div>
  );
}
